/**
 * ForgotPassword — request a password reset link.
 *
 * Response is intentionally generic (no account enumeration): the same success
 * message is shown whether or not the email exists on the platform.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../api';
import { ChevronLeft, Mail, ShieldCheck, CheckCircle } from 'lucide-react';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#595959';
const LINE = '#E5E5E5';
const SOFT = '#FAF9F6';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (!email || !/@/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setIsLoading(true);
    try {
      await authAPI.forgotPassword({ email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (e) {
      // Backend always returns 200 with a generic message — a thrown error here
      // means a transport failure, which we surface without leaking account info.
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: SOFT }}>
      <div className="px-5 py-4 flex items-center">
        <button data-testid="forgot-back-btn" onClick={() => navigate('/auth')}
          className="p-1.5 -ml-1.5">
          <ChevronLeft className="w-5 h-5" style={{ color: INK }} strokeWidth={1.5} />
        </button>
      </div>

      <div className="flex-1 px-6 pb-10 flex flex-col justify-center max-w-md mx-auto w-full">
        <div className="text-center mb-8">
          <img
            data-testid="forgot-brand-logo"
            src="/lunora_logo.png"
            alt="Lunora's"
            className="w-28 h-auto mx-auto mb-3"
            style={{ display: 'block' }}
          />
          <h1 className="text-[28px]"
            style={{ fontFamily: 'Playfair Display, serif', color: INK, fontWeight: 500 }}>
            {submitted ? 'Check your inbox' : 'Reset your password'}
          </h1>
          <p className="text-[12px] mt-2" style={{ color: MUTED, lineHeight: 1.6 }}>
            {submitted
              ? "If an account exists for that email, we've sent a reset link. It expires in 1 hour."
              : "Enter the email you signed up with — we'll send you a secure link to set a new password."}
          </p>
        </div>

        {submitted ? (
          <div className="space-y-4">
            <div data-testid="forgot-success-banner"
              className="flex items-center gap-3 px-4 py-3"
              style={{ background: 'rgba(22,101,52,0.06)', border: '1px solid rgba(22,101,52,0.30)' }}>
              <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#166534' }} strokeWidth={1.6} />
              <p className="text-[11px]" style={{ color: INK }}>
                Reset link sent (if the account exists).
              </p>
            </div>
            <button data-testid="forgot-back-signin-btn"
              onClick={() => navigate('/auth')}
              className="w-full py-3.5 text-[11px] tracking-[0.18em] uppercase text-white"
              style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}>
              Back to Sign In
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <label className="text-xs tracking-[0.15em] uppercase mb-2 block" style={{ color: MUTED }}>Email</label>
              <div className="relative">
                <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: MUTED }} strokeWidth={1.5} />
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                  placeholder="you@example.com"
                  className="w-full pl-8 py-3 text-sm bg-transparent border-0 border-b focus:outline-none"
                  style={{ borderColor: LINE, color: INK }} />
              </div>
            </div>

            {error && (
              <p data-testid="forgot-error" className="text-[11px] text-center" style={{ color: '#991B1B' }}>
                {error}
              </p>
            )}

            <button data-testid="forgot-submit-btn"
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full py-4 text-[11px] tracking-[0.18em] uppercase text-white disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 500 }}>
              {isLoading ? 'Sending…' : 'Send Reset Link'}
            </button>

            <div className="text-center">
              <button data-testid="forgot-signin-link"
                onClick={() => navigate('/auth')}
                className="text-xs" style={{ color: MUTED }}>
                Remember your password? <span style={{ color: GOLD }}>Sign In</span>
              </button>
            </div>

            <div className="flex items-center justify-center gap-2 pt-2">
              <ShieldCheck className="w-3.5 h-3.5" style={{ color: '#166534' }} strokeWidth={1.5} />
              <span className="text-[10px]" style={{ color: MUTED }}>Secure &amp; Encrypted</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
